/**
 * useExternalLink
 * One external user's personal guest link (current link, state, merged log)
 * and the admin actions on it, via `externalLinkApi`. Refetches after every
 * action; `externalUserId` null skips the read.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { externalLinksApi, ExternalLinkInfo, LinkEmailResult, LinkValidity } from '../api/externalLinksApi';

interface UseExternalLinkReturn {
    info: ExternalLinkInfo | null;
    loading: boolean;
    submitting: boolean;
    error: string | null;
    clearError: () => void;
    refetch: () => Promise<void>;
    issue: (validity: LinkValidity) => Promise<void>;
    setValidity: (validity: LinkValidity) => Promise<void>;
    /** Replace and e-mail the link; resolves to whether the e-mail went out. */
    rotate: (validity?: LinkValidity) => Promise<LinkEmailResult>;
    revoke: () => Promise<void>;
    send: () => Promise<LinkEmailResult>;
}

export function useExternalLink(externalUserId: string | null): UseExternalLinkReturn {
    const [info, setInfo] = useState<ExternalLinkInfo | null>(null);
    const [loading, setLoading] = useState(externalUserId !== null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Only the latest read may set state: a host that switches user (the
    // assign modals) must never show the previous user's link for the new one.
    const latestRequest = useRef(0);

    const refetch = useCallback(async () => {
        const request = ++latestRequest.current;
        if (!externalUserId) {
            setInfo(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const fetched = await externalLinksApi.get(externalUserId);
            if (request !== latestRequest.current) return;
            setInfo(fetched);
        } catch (err) {
            if (request !== latestRequest.current) return;
            console.error('Error fetching external link:', err);
            setError('Kunde inte hämta länken');
        } finally {
            if (request === latestRequest.current) setLoading(false);
        }
    }, [externalUserId]);

    useEffect(() => {
        setInfo(null);
        setError(null);
        void refetch();
    }, [refetch]);

    // Runs an action, then reloads the link and log. The error is kept for the
    // UI and rethrown so a modal can stay open.
    const run = useCallback(async <T,>(action: () => Promise<T>, fallback: string): Promise<T> => {
        if (!externalUserId) throw new Error('Ingen användare vald');
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await refetch();
            return result;
        } catch (err) {
            console.error('External link action failed:', err);
            setError(err instanceof Error && err.message ? err.message : fallback);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [externalUserId, refetch]);

    const token = info?.link?.token;
    const requireToken = useCallback((): string => {
        if (!token) throw new Error('Användaren har ingen länk');
        return token;
    }, [token]);

    return {
        info,
        loading,
        submitting,
        error,
        clearError: useCallback(() => setError(null), []),
        refetch,
        issue: useCallback(async (validity) => { await run(() => externalLinksApi.issue(externalUserId as string, validity), 'Kunde inte skapa länken'); }, [run, externalUserId]),
        setValidity: useCallback(async (validity) => { await run(() => externalLinksApi.setValidity(requireToken(), validity), 'Kunde inte ändra giltigheten'); }, [run, requireToken]),
        rotate: useCallback((validity) => run(() => externalLinksApi.rotate(requireToken(), validity), 'Kunde inte skicka ny länk'), [run, requireToken]),
        revoke: useCallback(async () => { await run(() => externalLinksApi.revoke(requireToken()), 'Kunde inte återkalla länken'); }, [run, requireToken]),
        send: useCallback(() => run(() => externalLinksApi.send(requireToken()), 'Kunde inte skicka länken'), [run, requireToken]),
    };
}
